import { useState, useEffect } from 'react';
import DashboardHeader from './DashboardHeader/DashboardHeader';
import './Dashboard.css';
import CaseSubmissionModal from './CaseSubmissionModal/CaseSubmissionModal';
import api from '../../service/apiService';
import { useNavigate } from 'react-router-dom';

const Dashboard = ({ user, onLogout }) => {
  // Global success notification shared with header callbacks
  const [successMessage, setSuccessMessage] = useState('');

  // Customer dashboard state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCaseType, setSelectedCaseType] = useState(null);
  const [caseTypes, setCaseTypes] = useState([]);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredType, setHoveredType] = useState(null);

  const navigate = useNavigate();

  // Header callbacks
  const handleAccountSuccess = (message) => {
    setSuccessMessage(message);
  };

  const handlePaymentMethodSuccess = (message) => {
    setSuccessMessage(message);
  };

  // Auto-hide success messages
  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(''), 4000);
    return () => clearTimeout(t);
  }, [successMessage]);

  // Customer dashboard lifecycle
  useEffect(() => {
    loadCases();
    loadCaseTypes();
    loadPaymentMethods();
  }, [user?.id]);

  const loadPaymentMethods = async () => {
    try {
      if (!user?.id) {
        setHasPaymentMethod(false);
        return;
      }
      const res = await api.getPaymentMethods(user.id);
      const methods = res?.paymentMethods || [];
      setHasPaymentMethod(methods.length > 0);
    } catch (err) {
      console.error('Failed to load payment methods:', err);
      setHasPaymentMethod(false);
    }
  };

  // Listen for global payment method changes (dispatched by PaymentMethodModal)
  useEffect(() => {
    const handler = () => {
      loadPaymentMethods();
    };

    window.addEventListener('paymentMethodsChanged', handler);
    return () => window.removeEventListener('paymentMethodsChanged', handler);
  }, [user?.id]);

  const loadCaseTypes = async () => {
    try {
      const res = await api.getCaseTypes();
      const types = res?.caseTypes || [];
      setCaseTypes(types);
    } catch (err) {
      console.error('Failed to load case types:', err);
      setCaseTypes([]);
    }
  };

  // Handle success message fade out (also used when submitting a case)
  const setTransientSuccess = (msg) => {
    setSuccessMessage(msg);
  };

  const loadCases = async () => {
    setIsLoading(true);
    try {
      const userCases = await api.getCases();

      const casesArray = userCases.cases || userCases || [];

      setCases(casesArray);
    } catch (error) {
      console.error('Failed to load cases:', error);
      setCases([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (caseType) => {
    setSelectedCaseType(caseType);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCaseType(null);
  };

  const handleSubmitCase = async (caseData) => {
    try {
      await api.createCase(caseData);
      handleCloseModal();
      await loadCases();
      setTransientSuccess('Case submitted successfully!');
    } catch (error) {
      console.error('Error submitting case:', error);
      throw new Error('Failed to submit case. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return '#f39c12';
      case 'in_progress':
        return '#3498db';
      case 'completed':
        return '#27ae60';
      case 'rejected':
        return '#e74c3c';
      default:
        return '#95a5a6';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Return a human-friendly label for a case type.
  // Prefer the API-provided display_name when available.
  const getCaseTypeLabel = (type) => {
    if (!type) return 'Case';
    const found = caseTypes.find((ct) => ct.name === type);
    if (found && found.display_name) return found.display_name;

    // Fallback: convert snake_case to Title Case
    return type
      .split('_')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  };

  return (
    <div className="dashboard-container">
      <DashboardHeader
        user={user}
        onLogout={onLogout}
        onAccountSuccess={handleAccountSuccess}
        onPaymentMethodSuccess={handlePaymentMethodSuccess}
      />

      {successMessage && <div className="success-notification">{successMessage}</div>}

      <main className="dashboard-main">
        <div className="dashboard-content">
          {/* Success Message (visual duplicate removed; handled above) */}

          <div className="welcome-section">
            <h2>Client Portal</h2>
            <p>Submit legal requests and track your cases.</p>
          </div>

          <div className="dashboard-grid">
            <div className="dashboard-card">
              <h3>My Cases</h3>
              <div className="stat-number">{cases.length}</div>
              <p>Total submitted cases</p>
            </div>

            <div className="dashboard-card">
              <h3>New Cases</h3>
              <div className="stat-number">
                {cases?.filter((c) => c.progress_status === 'new').length}
              </div>
              <p>Awaiting attorney review</p>
            </div>

            <div className="dashboard-card">
              <h3>In Progress</h3>
              <div className="stat-number">
                {cases?.filter((c) => c.progress_status === 'in_progress').length}
              </div>
              <p>Currently being handled</p>
            </div>

            <div className="dashboard-card">
              <h3>Completed</h3>
              <div className="stat-number">
                {cases?.filter((c) => c.progress_status === 'completed').length}
              </div>
              <p>Resolved cases</p>
            </div>
          </div>

          <div className="quick-actions">
            <h3>Submit New Request</h3>
            <div className="action-buttons">
              {caseTypes.map((ct) => (
                <div
                  key={ct.name}
                  className="case-type-wrap"
                  onMouseEnter={() => setHoveredType(ct.name)}
                  onMouseLeave={() => setHoveredType(null)}
                >
                  <button
                    className={`action-button primary ${!hasPaymentMethod ? 'disabled' : ''}`}
                    onClick={() => hasPaymentMethod && handleOpenModal(ct.name)}
                    aria-label={ct.display_name}
                    disabled={!hasPaymentMethod}
                    aria-disabled={!hasPaymentMethod}
                  >
                    {ct.display_name}
                  </button>

                  {/* Hover description box or disabled hint */}
                  {hoveredType === ct.name && (
                    <div className="case-type-tooltip">
                      {!hasPaymentMethod
                        ? 'You must add a payment method first. Go to the user menu → Payment Methods to add a card.'
                        : ct.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="recent-updates">
            <h3>My Cases</h3>
            {isLoading ? (
              <div className="loading-message">Loading cases...</div>
            ) : cases.length > 0 ? (
              <div className="cases-grid">
                {cases.map((case_) => (
                  <div key={case_.case_id} className="case-card customer">
                    <div className="case-card-header">
                      <div className="case-title-section">
                        <h4 className="case-title">{case_.case_title}</h4>
                        <span className="case-type-badge">{getCaseTypeLabel(case_.case_type)}</span>
                      </div>
                      <span
                        className="case-status-badge"
                        style={{ backgroundColor: getStatusColor(case_.progress_status) }}
                      >
                        {(case_.progress_status || 'unknown').replace('_', ' ').toUpperCase()}
                      </span>
                    </div>

                    <div className="case-card-body">
                      <div className="case-meta">
                        <div className="case-meta-item">
                          <span className="meta-label">Submitted:</span>
                          <span className="meta-value">{formatDate(case_.created_at)}</span>
                        </div>
                        {case_.urgency_level && (
                          <div className="case-meta-item">
                            <span className="meta-label">Urgency:</span>
                            <span className={`meta-value urgency-${case_.urgency_level}`}>
                              {case_.urgency_level.charAt(0).toUpperCase() +
                                case_.urgency_level.slice(1)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="case-card-footer">
                      <button
                        className="case-view-btn"
                        onClick={() => navigate(`/cases/${case_.case_id}`)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                        </svg>
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <h4>No Cases Yet</h4>
                <p>
                  You haven't submitted any cases yet. Click the buttons above to submit your first
                  legal request.
                </p>
              </div>
            )}
          </div>

          <CaseSubmissionModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            caseType={selectedCaseType}
            onSubmit={handleSubmitCase}
            userId={user.id}
          />
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
